import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, shareReplay, switchMap, map, of, forkJoin, catchError } from 'rxjs';
import {
  WikiManualManifest,
  WikiManualNode,
  WikiManualPage,
  WikiManualSearchResult,
} from './wiki-manual.models';

@Injectable({ providedIn: 'root' })
export class WikiManualService {
  private manifest$: Observable<WikiManualManifest>;
  private contentCache = new Map<string, string>();

  constructor(private http: HttpClient) {
    this.manifest$ = this.http
      .get<WikiManualManifest>('assets/wiki-manual/wiki-manifest.json')
      .pipe(shareReplay(1));
  }

  getManifest(): Observable<WikiManualManifest> {
    return this.manifest$;
  }

  getPageContent(file: string): Observable<string> {
    if (this.contentCache.has(file)) {
      return of(this.contentCache.get(file)!);
    }
    return this.http.get(`assets/wiki-manual/${file}`, { responseType: 'text' }).pipe(
      map(content => {
        const stripped = content.replace(/^---[\s\S]*?---\r?\n?/, '');
        this.contentCache.set(file, stripped);
        return stripped;
      }),
      catchError(err => {
        console.warn(`WikiManualService: failed to load ${file}:`, err.message ?? err);
        return of('');
      })
    );
  }

  getAdjacentPages(pageId: string, pages: WikiManualPage[]): { prev?: WikiManualPage; next?: WikiManualPage } {
    if (pages.length === 0) return {};
    const idx = pages.findIndex(p => p.id === pageId);
    return {
      prev: idx > 0 ? pages[idx - 1] : undefined,
      next: idx < pages.length - 1 ? pages[idx + 1] : undefined,
    };
  }

  getBreadcrumb(pageId: string, tree: WikiManualNode[]): WikiManualNode[] {
    const result: WikiManualNode[] = [];
    function search(nodes: WikiManualNode[]): boolean {
      for (const node of nodes) {
        if (node.id === pageId) {
          result.push(node);
          return true;
        }
        if (node.children?.length && search(node.children)) {
          result.unshift(node);
          return true;
        }
      }
      return false;
    }
    search(tree);
    return result;
  }

  searchPages(query: string): Observable<WikiManualSearchResult[]> {
    if (!query.trim()) return of([]);
    return this.manifest$.pipe(
      switchMap(manifest => {
        const lower = query.toLowerCase();
        const fetches = manifest.pages.map(page =>
          this.getPageContent(page.file).pipe(
            map(content => {
              const idx = content.toLowerCase().indexOf(lower);
              if (idx === -1 && !page.title.toLowerCase().includes(lower)) return null;
              const start = Math.max(0, idx - 60);
              const end = Math.min(content.length, idx + 90);
              const snippet =
                idx !== -1
                  ? '...' + content.slice(start, end).replace(/\n/g, ' ') + '...'
                  : page.description || '';
              return { page, snippet } as WikiManualSearchResult;
            })
          )
        );
        return forkJoin(fetches).pipe(
          map(results => results.filter((r): r is WikiManualSearchResult => r !== null))
        );
      })
    );
  }
}
